import { formatAmount } from '../billing/calc.js';
import { ORDER_TYPES } from '../constants/pos.js';
import { formatInZone } from '../time/index.js';
import { reportCellText, reportSummaryText } from '../reports/format.js';

/** Thermal templates for shift (X / close) and day (Z) reports. Models: reports/shift.js, server Z. */
const a = (p) => formatAmount(p ?? 0);
const when = (d, tz) => (d ? formatInZone(d, tz, 'dd/MM/yyyy hh:mm a') : '-');
const signed = (p) => (p > 0 ? `+${a(p)}` : a(p));

export function shiftReportDocument(r, { profile }) {
  const tz = r.timezone;
  const b = [
    { t: 'text', text: r.businessName, align: 'center', bold: true },
    r.outletName && { t: 'text', text: r.outletName, align: 'center' },
    { t: 'rule' },
    { t: 'text', text: r.title, align: 'center', bold: true, h: 2 },
    { t: 'rule' },
    { t: 'pair', left: 'Shift', right: `#${r.shiftNo}${r.tillLabel ? ` · ${r.tillLabel}` : ''}` },
    { t: 'pair', left: 'Opened', right: `${when(r.openedAt, tz)}` },
    r.openedBy && { t: 'pair', left: 'Opened by', right: r.openedBy },
    r.closedAt && { t: 'pair', left: 'Closed', right: when(r.closedAt, tz) },
    r.closedBy && { t: 'pair', left: 'Closed by', right: r.closedBy },
    r.cashiers?.length && { t: 'pair', left: 'Cashiers', right: r.cashiers.join(', ') },
    { t: 'pair', left: 'Printed', right: when(r.printedAt, tz) },
    { t: 'rule' },
    { t: 'text', text: 'SALES', bold: true },
    { t: 'pair', left: `Bills (${r.sales.billCount})`, right: a(r.sales.salesPaise) },
    r.sales.discountPaise > 0 && { t: 'pair', left: '  incl. discounts', right: a(r.sales.discountPaise) },
    { t: 'pair', left: '  incl. GST', right: a(r.sales.taxPaise) },
    r.sales.voidCount > 0 && { t: 'pair', left: `Voids (${r.sales.voidCount})`, right: `-${a(r.sales.voidPaise)}` },
    r.sales.refundCount > 0 && {
      t: 'pair',
      left: `Refunds (${r.sales.refundCount})`,
      right: `-${a(r.sales.refundPaise)}`,
    },
    { t: 'pair', left: 'NET SALES', right: a(r.sales.netPaise), bold: true },
    { t: 'rule' },
    { t: 'text', text: 'PAYMENTS (NET)', bold: true },
    ...(r.payments.length
      ? r.payments.map((p) => ({ t: 'pair', left: p.label, right: a(p.amountPaise) }))
      : [{ t: 'text', text: 'No payments' }]),
    { t: 'rule' },
    { t: 'text', text: 'CASH DRAWER', bold: true },
    { t: 'pair', left: 'Opening float', right: a(r.cash.openingFloatPaise) },
    { t: 'pair', left: 'Cash sales', right: a(r.cash.salesPaise) },
    r.cash.refundsPaise > 0 && { t: 'pair', left: 'Cash refunds', right: `-${a(r.cash.refundsPaise)}` },
    r.cash.voidsPaise > 0 && { t: 'pair', left: 'Cash voids', right: `-${a(r.cash.voidsPaise)}` },
    r.cash.duesPaise > 0 && { t: 'pair', left: 'Dues collected', right: a(r.cash.duesPaise) },
    r.cash.inPaise > 0 && { t: 'pair', left: 'Cash in', right: a(r.cash.inPaise) },
    r.cash.outPaise > 0 && { t: 'pair', left: 'Cash out', right: `-${a(r.cash.outPaise)}` },
    ...(r.cashOut ?? []).map((e) => ({ t: 'pair', left: `  ${e.category}`, right: a(e.amountPaise) })),
    { t: 'pair', left: 'EXPECTED IN DRAWER', right: a(r.cash.expectedPaise), bold: true },
  ];
  if (r.cash.countedPaise != null) {
    b.push(
      { t: 'rule' },
      { t: 'text', text: 'CLOSING COUNT', bold: true },
      ...r.denominations.map((d) => ({ t: 'pair', left: `  ${d.value} x ${d.count}`, right: a(d.amountPaise) })),
      r.otherPaise > 0 && { t: 'pair', left: '  Coins / other', right: a(r.otherPaise) },
      { t: 'pair', left: 'COUNTED', right: a(r.cash.countedPaise), bold: true },
      {
        t: 'pair',
        left: r.cash.variancePaise === 0 ? 'VARIANCE' : r.cash.variancePaise > 0 ? 'EXCESS' : 'SHORT',
        right: signed(r.cash.variancePaise),
        bold: true,
        h: 2,
      },
    );
  }
  b.push(
    { t: 'rule' },
    { t: 'blank' },
    { t: 'text', text: 'Signature: ________________', align: profile.charsPerLine >= 42 ? 'right' : 'left' },
  );
  return { blocks: b.filter(Boolean) };
}

const typeLabel = (k) => ORDER_TYPES.find((o) => o.key === k)?.label ?? k;

/**
 * Z report (end of day). z: { zNo, businessDate, businessName, outletName, timezone, closedAt,
 * closedBy, sales, payments[], orderTypes[], taxBreakup[], refunds, voids, invoiceRange,
 * shifts[], cash, expenses[] } — built by the server at day close.
 */
export function dayReportDocument(z, { profile }) {
  const tz = z.timezone;
  const wide = profile.charsPerLine >= 42;
  const s = z.sales;
  const b = [
    { t: 'text', text: z.businessName, align: 'center', bold: true },
    z.outletName && { t: 'text', text: z.outletName, align: 'center' },
    { t: 'rule' },
    { t: 'text', text: `Z REPORT #${z.zNo}`, align: 'center', bold: true, w: 2, h: 2 },
    { t: 'text', text: `Business day ${z.businessDate}`, align: 'center' },
    { t: 'rule' },
    { t: 'pair', left: 'Closed', right: when(z.closedAt, tz) },
    z.closedBy && { t: 'pair', left: 'Closed by', right: z.closedBy },
    z.invoiceRange?.first && { t: 'pair', left: 'Bills', right: `${z.invoiceRange.first}` },
    z.invoiceRange?.last && { t: 'pair', left: '', right: `to ${z.invoiceRange.last}` },
    { t: 'rule' },
    { t: 'text', text: 'SALES', bold: true },
    { t: 'pair', left: `Bills`, right: String(s.billCount) },
    { t: 'pair', left: 'Items sold', right: String(s.itemCount) },
    { t: 'pair', left: 'Gross (item total)', right: a(s.grossPaise) },
    s.discountPaise > 0 && { t: 'pair', left: 'Discounts', right: `-${a(s.discountPaise)}` },
    s.chargesPaise > 0 && { t: 'pair', left: 'Charges', right: a(s.chargesPaise) },
    { t: 'pair', left: 'Taxable value', right: a(s.taxablePaise) },
    s.cgstPaise > 0 && { t: 'pair', left: 'CGST', right: a(s.cgstPaise) },
    s.sgstPaise > 0 && { t: 'pair', left: 'SGST', right: a(s.sgstPaise) },
    s.igstPaise > 0 && { t: 'pair', left: 'IGST', right: a(s.igstPaise) },
    s.cessPaise > 0 && { t: 'pair', left: 'Cess', right: a(s.cessPaise) },
    s.roundOffPaise !== 0 && { t: 'pair', left: 'Round off', right: a(s.roundOffPaise) },
    { t: 'pair', left: 'TOTAL SALES', right: a(s.totalPaise), bold: true, h: 2 },
    z.voids?.count > 0 && { t: 'pair', left: `Voided bills (${z.voids.count})`, right: a(z.voids.paise) },
    z.refunds?.count > 0 && { t: 'pair', left: `Refunds (${z.refunds.count})`, right: `-${a(z.refunds.paise)}` },
    z.refunds?.count > 0 && {
      t: 'pair',
      left: 'NET AFTER REFUNDS',
      right: a(s.totalPaise - z.refunds.paise),
      bold: true,
    },
    { t: 'pair', left: 'Average bill', right: a(s.avgBillPaise) },
    { t: 'rule' },
    { t: 'text', text: 'PAYMENTS', bold: true },
    ...z.payments.map((p) => ({ t: 'pair', left: p.label, right: a(p.amountPaise) })),
  ];
  if (z.orderTypes?.length) {
    b.push({ t: 'rule' }, { t: 'text', text: 'ORDER TYPES', bold: true });
    for (const o of z.orderTypes) b.push({ t: 'pair', left: `${typeLabel(o.key)} (${o.count})`, right: a(o.paise) });
  }
  if (z.taxBreakup?.length) {
    b.push(
      { t: 'rule' },
      { t: 'text', text: 'GST SUMMARY', bold: true },
      {
        t: 'cols',
        bold: true,
        cols: [
          { text: 'Rate', w: wide ? 8 : 6 },
          { text: 'Taxable', w: '*', align: 'right' },
          { text: 'Tax', w: wide ? 11 : 9, align: 'right' },
        ],
      },
      ...z.taxBreakup.map((r) => ({
        t: 'cols',
        cols: [
          { text: r.rate, w: wide ? 8 : 6 },
          { text: a(r.taxablePaise), w: '*', align: 'right' },
          { text: a(r.taxPaise), w: wide ? 11 : 9, align: 'right' },
        ],
      })),
    );
  }
  b.push(
    { t: 'rule' },
    { t: 'text', text: 'CASH', bold: true },
    { t: 'pair', left: 'Opening floats', right: a(z.cash.openingFloatPaise) },
    { t: 'pair', left: 'Cash sales', right: a(z.cash.salesPaise) },
    z.cash.refundsPaise > 0 && { t: 'pair', left: 'Cash refunds', right: `-${a(z.cash.refundsPaise)}` },
    z.cash.voidsPaise > 0 && { t: 'pair', left: 'Cash voids', right: `-${a(z.cash.voidsPaise)}` },
    z.cash.duesPaise > 0 && { t: 'pair', left: 'Dues collected', right: a(z.cash.duesPaise) },
    z.cash.inPaise > 0 && { t: 'pair', left: 'Cash in', right: a(z.cash.inPaise) },
    z.cash.outPaise > 0 && { t: 'pair', left: 'Cash out / expenses', right: `-${a(z.cash.outPaise)}` },
    ...(z.expenses ?? []).map((e) => ({ t: 'pair', left: `  ${e.category}`, right: a(e.amountPaise) })),
    { t: 'pair', left: 'Expected', right: a(z.cash.expectedPaise) },
    { t: 'pair', left: 'Counted', right: a(z.cash.countedPaise) },
    { t: 'pair', left: 'VARIANCE', right: signed(z.cash.variancePaise), bold: true },
  );
  if (z.shifts?.length) {
    b.push({ t: 'rule' }, { t: 'text', text: 'SHIFTS', bold: true });
    for (const sh of z.shifts)
      b.push({
        t: 'pair',
        left: `#${sh.shiftNo} ${sh.cashiers.join(', ')}`.slice(0, Math.max(10, profile.charsPerLine - 12)),
        right: signed(sh.variancePaise),
      });
  }
  b.push(
    { t: 'rule' },
    { t: 'blank' },
    { t: 'text', text: 'Signature: ________________', align: wide ? 'right' : 'left' },
  );
  return { blocks: b.filter(Boolean) };
}

/**
 * Any report on the thermal printer: header, summary figures, then the first column and the
 * column marked print: true (or the last money column), with totals. Long reports are cut at
 * maxRows lines (the screen, Excel and PDF have everything).
 */
export function reportSummaryDocument(report, meta, { maxRows = 60 } = {}) {
  const tz = meta.tz;
  const first = report.columns[0];
  const value =
    report.columns.find((c) => c.print) ??
    [...report.columns].reverse().find((c) => c.type === 'money') ??
    report.columns[1];
  const b = [
    meta.businessName && { t: 'text', text: meta.businessName, align: 'center', bold: true },
    { t: 'text', text: meta.outletLabel, align: 'center' },
    { t: 'rule' },
    { t: 'text', text: report.title.toUpperCase(), align: 'center', bold: true },
    { t: 'text', text: meta.rangeLabel, align: 'center' },
    { t: 'rule' },
  ];
  for (const s of report.summary ?? []) b.push({ t: 'pair', left: s.label, right: reportSummaryText(s, tz) });
  if (value && value !== first && report.rows.length) {
    b.push({ t: 'rule' }, { t: 'pair', left: first.label || 'Item', right: value.label, bold: true });
    for (const r of report.rows.slice(0, maxRows))
      b.push({
        t: 'pair',
        left: reportCellText(first, r[first.key], tz),
        right: reportCellText(value, r[value.key], tz),
        bold: !!r.bold,
      });
    if (report.rows.length > maxRows) b.push({ t: 'text', text: `... ${report.rows.length - maxRows} more rows` });
    if (report.totals?.[value.key] != null)
      b.push({ t: 'pair', left: 'TOTAL', right: reportCellText(value, report.totals[value.key], tz), bold: true });
  }
  b.push({ t: 'rule' }, { t: 'pair', left: 'Printed', right: when(new Date(), tz) });
  return { blocks: b.filter(Boolean) };
}
